import React, { useEffect, useState } from "react";
import { PieChart, Pie, Sector, ResponsiveContainer } from "recharts";
import "./ClientPortfolioAnafQuery.css";
import axiosInstance from "../../../Config/axiosInstance";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

function ClientPortfolioAnafQuery({ clientData }) {
  const [loadingAnaf, setLoadingAnaf] = useState(true);
  const [anafError, setAnafError] = useState("");

  // CONSTS:
  const [address, setAddress] = useState('')
  const [tva, setTva] = useState('')
  const [employees, setEmployees] = useState('')
  const [year, setYear] = useState('')

  const [turnover, setTurnover] = useState('')
  const [netResult, setNetResult] = useState('')
  const [totalDebts, setTotalDebts] = useState('')
  const [equity, setEquity] = useState('')
  const [currentRatio, setCurrentRatio] = useState('')

  const [activeProjects, setActiveProjects] = useState('')
  const [debtToEquity, setDebtToEquity] = useState('')
  const [netMargin, setNetMargin] = useState('')
  const [isTurnover, setIsTurnover] = useState(false)
  const [trendEvolution, setTrendEvolution] = useState('')

  const [procentEquity, setProcentEquity] = useState('')
  const [procentDebt, setProcentDebt] = useState('')
  const [total, setTotal] = useState('')

  const data = [
    { name: "Total Liabilities", value: procentDebt, color: "#E94E3C" },
    { name: "Equity", value: procentEquity, color: "#1FB67A" },
  ];

  // TAGS
  const [highRisk, setHighRisk] = useState(false)
  const [negativeEquity, setNegativeEquity] = useState(false)
  const [liquidityAndLoss, setLiquidityAndLoss] = useState(false)
  const [highLeverage, setHighLeverage] = useState(false)

  const [mediumRisk, setMediumRisk] = useState(false)
  const [lowBufferLiquidity, setLowBufferLiquidity] = useState(false)
  const [moderateLeverage, setModerateLeverage] = useState(false)
  const [lowMargin, setLowMargin] = useState(false)
  const [negativeTrend, setNegativeTrend] = useState(false)

  const [lowRisk, setLowRisk] = useState(false)
  
  const [unknownRisk, setUnknownRisk] = useState(false)
  

  const formatTotal = (value) => {
    if (value === null) {
      return null;
    }
    if (value >= 1_000_000_000_000) {
      return `${(value / 1_000_000_000_000).toFixed(1)}T`.replace('.', ',');
    }
    if (value >= 1_000_000_000) {
      return `${(value / 1_000_000_000).toFixed(1)}B`.replace('.', ',');
    }
    if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(1)}M`.replace('.', ',');
    }
    if (value >= 1_000) {
      return `${(value / 1_000).toFixed(1)}K`.replace('.', ',');
    }
    return value.toString().replace('.', ',');
  };


  useEffect(() => {
    const fetchAnafData = async () => {
      try {
        setLoadingAnaf(true);
        const response = await axiosInstance.get("/anaf-query/query/", {
          params: { client: clientData.id }
        });

        const tva = response.data.tva
        const bilant = response.data.bilant
        const bilant_vechi = response.data.bilant_vechi

        let tempCurrentRatio = null
        let tempDebtToEquity = null
        let tempNetMargin = null


        console.log(response.data)

        // Identification details:
        setAddress(tva.adresa_completa)
        setTva(tva.platitor_tva)
        setEmployees(bilant.angajati ? bilant.angajati.toLocaleString('en-US') : null)
        setYear(bilant.an)

        // Financial health:
        setTurnover(bilant.cifra_afaceri ? bilant.cifra_afaceri.toLocaleString().replace(/,/g, '.') : null)
        if (bilant.profit_net === 0) {
          setNetResult(bilant.pierdere_neta.toLocaleString().replace(/,/g, '.'))
        }
        else if (bilant.pierdere_neta === 0) {
          setNetResult(bilant.profit_net.toLocaleString().replace(/,/g, '.'))
        }
        else {
          setNetResult(null)
        }
        setTotalDebts(bilant.datorii ? bilant.datorii.toLocaleString().replace(/,/g, '.') : null)
        setEquity(bilant.capitaluri_total ? bilant.capitaluri_total.toLocaleString().replace(/,/g, '.') : null)
        if (bilant.active_circulante !== null && bilant.datorii !== null) {
          setCurrentRatio((bilant.active_circulante / bilant.datorii).toFixed(2).replace('.', ','))
          tempCurrentRatio = (bilant.active_circulante / bilant.datorii).toFixed(2)
        }
        else {
          setCurrentRatio(null)
        }
        
        // Credit & Risk Overview
        if (bilant.active_circulante !== null && bilant.datorii !== null) {
          setActiveProjects((bilant.active_circulante / Math.max(bilant.datorii, 1)).toFixed(2).replace('.', ','))
        }
        else {
          setActiveProjects(null)
        }
        
        if (bilant.datorii !== null && bilant.capitaluri_total !== null) {
          setDebtToEquity((bilant.datorii / Math.max(bilant.capitaluri_total, 1)).toFixed(2).replace('.', ','))
          tempDebtToEquity = (bilant.datorii / Math.max(bilant.capitaluri_total, 1)).toFixed(2)
        }
        else {
          setDebtToEquity(null)
        }

        if (bilant.profit_net > 0) {
          const calcNetMargin = (bilant.profit_net / Math.max(bilant.cifra_afaceri, 1)) * 100
          setNetMargin(parseFloat(calcNetMargin).toFixed(2).replace('.', ','))
          tempNetMargin = parseFloat(calcNetMargin).toFixed(2)
        }
        else if (bilant.pierdere_neta > 0) {
          const calcNetMargin = (-bilant.pierdere_neta / Math.max(bilant.cifra_afaceri, 1)) * 100
          setNetMargin(parseFloat(calcNetMargin).toFixed(2).replace('.', ','))
          tempNetMargin = parseFloat(calcNetMargin).toFixed(2)
        }
        else {
          setNetMargin(null)
        }

        if (bilant_vechi.profit_net === 0) {
          const calcTrendEvolution = (((bilant.cifra_afaceri - bilant_vechi.cifra_afaceri) / Math.max(bilant_vechi.cifra_afaceri, 1)) * 100)
          setTrendEvolution(parseFloat(calcTrendEvolution).toFixed(2).replace('.', ','))
          setIsTurnover(true)
        }
        else if (bilant_vechi.profit_net > 0) {
          const calcTrendEvolution = (((bilant.profit_net - bilant_vechi.profit_net) / Math.max(bilant_vechi.profit_net, 1)) * 100)
          setTrendEvolution(parseFloat(calcTrendEvolution).toFixed(2).replace('.', ','))
          setIsTurnover(false)
        }
        else {
          setTrendEvolution(null)
          setIsTurnover(false)
        }

        // Capital Structure Overview
        setProcentEquity(bilant.capitaluri_total ? bilant.capitaluri_total : null)
        setProcentDebt(bilant.datorii ? bilant.datorii : null)
        if (bilant.capitaluri_total !== null && bilant.datorii !== null) {
          setTotal(formatTotal(bilant.capitaluri_total + bilant.datorii))
        }
        else {
          setTotal(null)
        }
        
        if (bilant.cifra_afaceri === null ||
          bilant.profit_net === null ||
          bilant.pierdere_neta === null ||
          bilant.datorii === null ||
          bilant.active_circulante === null ||
          bilant.capitaluri_total === null
        ) {
          setUnknownRisk(true)
        }
        else {
          // Tag settings
          // HIGH RISK:
          if (bilant.capitaluri_total <= 0) {
            setNegativeEquity(true)
          }
          if (currentRatio < 1.0) {
            setLiquidityAndLoss(true)
          }
          if (debtToEquity > 3.0) {
            setHighLeverage(true)
          }
          // MEDIUM RISK:
          if (currentRatio >= 1.0 && currentRatio < 1.2) {
            setLowBufferLiquidity(true)
          }
          if (debtToEquity >= 1.5 && debtToEquity <= 3.0) {
            setModerateLeverage(true)
          }
          if (netMargin >= -0.01 && netMargin <= 0.01) {
            setLowMargin(true)
          }
          if (bilant_vechi.profit_net === 0) {
            if (trendEvolution < 10) {
              setNegativeTrend(true)
            }
          }
          else {
            if (trendEvolution < 20) {
              setNegativeTrend(true)
            }
          }

          if (bilant.capitaluri_total <= 0 || tempCurrentRatio < 1.0 || tempDebtToEquity > 3.0) {
            setHighRisk(true)
            await axiosInstance.post("/anaf-query/risk/", {
              id: bilant.id, risk: 'HIGH' 
            });
          }
          else if ((tempCurrentRatio >= 1.0 && tempCurrentRatio < 1.2) || (tempDebtToEquity >= 1.5 && tempDebtToEquity <= 3.0) || (tempNetMargin >= -0.01 && tempNetMargin <= 0.01) || bilant_vechi.profit_net === 0) {
            setMediumRisk(true)
            await axiosInstance.post("/anaf-query/risk/", {
              id: bilant.id, risk: 'MEDIUM' 
            });
          }
          else if (
            bilant.capitaluri_total > 0 &&
            tempCurrentRatio >= 1.2 &&
            bilant.datorii <= bilant.active_circulante &&
            bilant.pierdere_neta === 0
          ) {
            setLowRisk(true)
            await axiosInstance.post("/anaf-query/risk/", {
              id: bilant.id, risk: 'LOW' 
            });
          }
        }
      } catch (error) {
        if (error.status === 404) {
          setAnafError("CUI not found in the Anaf Database.")
        }
        else {
          setAnafError(error.message);
        }
      } finally {
        setLoadingAnaf(false);
      }
    };

    fetchAnafData();
    // Necesar pentru a scapa de warning
    // Daca rezolv cum indica warning-ul functia merge eronat deoarece trebuie sa ruleze doar o data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadingAnaf) {
    return (
      <div className="anaf-query-state">
        <QueryStatsIcon className="anaf-query-state-icon anaf-query-loading-spin" />
        <p>Se încarcă...</p>
      </div>
    );
  }

  if (anafError) {
    return (
      <div className="anaf-query-state anaf-query-error-state">
        <ErrorOutlineIcon className="anaf-query-state-icon" />
        <p>{anafError}</p>
      </div>
    );
  }

  return (
    <div className="anaf-query">
      <div className="anaf-query-single-card">
        <div className="anaf-query-text-line">
          <p>Most recent Anaf financial data from: <b>{year}</b></p>
        </div>
        <div className="anaf-query-tag-line">
          {highRisk ?
            <span className={`anaf-query-tag anaf-query-tag--danger`}>
              <span className="anaf-query-tag-dot" />
              HIGH_RISK
            </span>
            : null
          }
          {negativeEquity ?
            <span className={`anaf-query-tag anaf-query-tag--amber`}>
              <span className="anaf-query-tag-dot" />
              NEGATIVE_LIQUIDITY
            </span>
            : null
          }
          {liquidityAndLoss ?
            <span className={`anaf-query-tag anaf-query-tag--amber`}>
              <span className="anaf-query-tag-dot" />
              LIQUIDITY_AND_LOSS
            </span>
            : null
          }
          {highLeverage ?
            <span className={`anaf-query-tag anaf-query-tag--amber`}>
              <span className="anaf-query-tag-dot" />
              HIGH_LEVERAGE
            </span>
            : null
          }
          {mediumRisk & !highRisk ?
            <span className={`anaf-query-tag anaf-query-tag--teal`}>
              <span className="anaf-query-tag-dot" />
              MEDIUM_RISK
            </span>
            : null
          }
          {lowBufferLiquidity ?
            <span className={`anaf-query-tag anaf-query-tag--teal`}>
              <span className="anaf-query-tag-dot" />
              LOW_BUFFER_LIQUIDITY
            </span>
            : null
          }
          {moderateLeverage ?
            <span className={`anaf-query-tag anaf-query-tag--teal`}>
              <span className="anaf-query-tag-dot" />
              MODERATE_LEVERAGE
            </span>
            : null
          }
          {lowMargin ?
            <span className={`anaf-query-tag anaf-query-tag--teal`}>
              <span className="anaf-query-tag-dot" />
              LOW_MARGIN
            </span>
            : null
          }
          {negativeTrend ?
            <span className={`anaf-query-tag anaf-query-tag--teal`}>
              <span className="anaf-query-tag-dot" />
              NEGATIVE_TREND
            </span>
            : null
          }
          {lowRisk & !mediumRisk & !highRisk ?
            <span className={`anaf-query-tag anaf-query-tag--blue`}>
              <span className="anaf-query-tag-dot" />
              LOW_RISK
            </span>
            : null
          }
          {unknownRisk ?
            <span className={`anaf-query-tag anaf-query-tag--blue`}>
              <span className="anaf-query-tag-dot" />
              UNKNOWN_RISK
            </span>
            : null
          }
        </div>
      </div>

      <div className="anaf-query-grid">
        <div className="anaf-query-card">
          <div className="anaf-query-card-header">
            <div>
              <h2>Identification Details</h2>
            </div>
          </div>

          <div className="anaf-query-section">
            <h3>Address</h3>
              <div className="anaf-query-address">
                {address}
              </div>
          </div>

          <div className="anaf-query-summary-grid">
            <div className="anaf-query-summary-box">
              <span className="anaf-query-summary-label">TVA</span>
              {tva != null && (tva ? 
                <strong className="anaf-query-summary-value-amber">
                  Inscris in scopuri de TVA (Lunar)
                </strong> :
                <strong className="anaf-query-summary-value-danger">
                  Neinscris in scopuri de TVA (Lunar)
                </strong>
              )}
              {(tva === null ? 
                <strong className="anaf-query-summary-value-amber">
                  N/A
                </strong> : null
              )}
            </div>

            <div className="anaf-query-summary-box">
              <span className="anaf-query-summary-label">Employees</span>
              <strong className="anaf-query-summary-value">
                {employees ? employees : 'N/A'}
              </strong>
            </div>
          </div>
        </div>

        <div className="anaf-query-card">
          <div className="anaf-query-card-header">
            <div>
              <h2>Financial health</h2>
            </div>
          </div>

          <div className="anaf-query-section-total">
            <div className="anaf-query-total">
              <h3>Turnover</h3>
              <h3 style={{ color: turnover && turnover.replace(/\./g, '') < 1_000_000 ? '#E94E3C' : '#4b5563' }}>
                {turnover ? `${turnover} RON` : 'N/A'}
              </h3>
            </div>
          </div>

          <div className="anaf-query-section-total">
            <div className="anaf-query-total">
              <h3>NET Result</h3>
              <h3>{netResult ? `${netResult} RON` : 'N/A'}</h3>
            </div>
          </div>

          <div className="anaf-query-section-total">
            <div className="anaf-query-total">
              <h3>Total Debts</h3>
              <h3>{totalDebts ? `${totalDebts} RON` : 'N/A'}</h3>
            </div>
          </div>

          <div className="anaf-query-section-total">
            <div className="anaf-query-total">
              <h3>Equity</h3>
              <h3 style={{ color: equity && equity.replace(/\./g, '') <= 0 ? '#E94E3C' : '#4b5563' }}>
                {equity ? `${equity} RON` : 'N/A'}
              </h3>
            </div>
          </div>

          <div className="anaf-query-fancy-total">
            <div className="anaf-query-total">
              <h3>Current Ratio</h3>
              <h3 style={{ color: currentRatio && parseFloat(currentRatio.replace(',', '.')) < 1.0 ? '#E94E3C' : '#4b5563' }}>
                {currentRatio ?? 'N/A'}
              </h3>
            </div>
          </div>
        </div>

        <div className="anaf-query-card">
          <div className="anaf-query-card-header">
            <div>
              <h2>Credit & Risk Overview</h2>
            </div>
          </div>

          <div className="anaf-query-projects-wrapper">
            <div className="anaf-query-risk-entry">
              <h3>Current Ratio</h3>
              <div className="anaf-query-timeline-description">
                Current Assets/Liabilities. A level below 1.0 indicates a major risk, with the company unable to cover its liabilities from current assets.
              </div>
            </div>

            <div className={`anaf-query-risk-value ${activeProjects && parseFloat(activeProjects.replace(',', '.')) < 1.0 ? `anaf-query-risk-value--danger` : `anaf-query-risk-value--amber`}`}>
              <h3>Value:&nbsp;</h3>
              <h3>{activeProjects ?? 'N/A'}</h3>
            </div>
          </div>

          <div className="anaf-query-projects-wrapper">
            <div className="anaf-query-risk-entry">
              <h3>Debt to Equity</h3>
              <div className="anaf-query-timeline-description">
                Total Debt/Equity. A level above 3.0 indicates a dangerous dependence on external financing.
              </div>
            </div>

            <div className={`anaf-query-risk-value ${debtToEquity && parseFloat(debtToEquity.replace(',', '.')) > 3.0 ? `anaf-query-risk-value--danger` : `anaf-query-risk-value--amber`}`}>
              <h3>Value:&nbsp;</h3>
              <h3>{debtToEquity ?? 'N/A'}</h3>
            </div>
          </div>

          <div className="anaf-query-projects-wrapper">
            <div className="anaf-query-risk-entry">
              <h3>Net Margin</h3>
              <div className="anaf-query-timeline-description">
                Net Result/Turnover. Negative margins or margins very close to 0% denote a lack of operational profitability.
              </div>
            </div>

            <div className={`anaf-query-risk-value ${netMargin && parseFloat(netMargin.replace(',', '.')) < 0 ? `anaf-query-risk-value--danger` : `anaf-query-risk-value--amber`}`}>
              <h3>Value:&nbsp;</h3>
              <h3>{netMargin ? `${netMargin}%` : 'N/A'}</h3>
            </div>
          </div>

          <div className="anaf-query-projects-wrapper">
            <div className="anaf-query-risk-entry">
              <h3>Trend Evolution</h3>
              <div className="anaf-query-timeline-description">
                Compared to the previous year. Dramatic decreases in turnover <span>{'(>20%)'}</span> or profit indicate a decline in business.
              </div>
            </div>

            <div className={`anaf-query-risk-value ${trendEvolution && parseFloat(trendEvolution.replace(',', '.')) < -20 ? `anaf-query-risk-value--danger` : `anaf-query-risk-value--amber`}`}>
              {isTurnover ? <h3>Turnover:&nbsp;</h3> : <h3>Profit:&nbsp;</h3>}
              <h3>{trendEvolution ? `${trendEvolution}%` : 'N/A'}</h3>
            </div>
          </div>
        </div>

        <div className="anaf-query-card">
          <div className="anaf-query-card-header">
            <div>
              <h2>Capital Structure Overview</h2>
            </div>
          </div>

          <div className="anaf-query-pie-chart-wrapper">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={100}
                  paddingAngle={0}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                  stroke="none"
                  animationBegin={0}
                  animationDuration={500}
                  shape={(props) => (
                    <Sector {...props} fill={props.payload.color} />
                  )}
                />
              </PieChart>
            </ResponsiveContainer>

            <div className="anaf-query-pie-chart-center-overlay">
              <span className="anaf-query-pie-chart-center-label">Total</span>
              <span className="anaf-query-pie-chart-center-value">
                {total ? `${formatTotal(total)} RON` : 'N/A'}
              </span>
            </div>
          </div>

          <div className="anaf-query-pie-chart-legend">
            <div className="anaf-query-pie-chart-legend-item">
              <span
                className="anaf-query-pie-chart-legend-swatch"
                style={{ backgroundColor: "#1FB67A" }}
              />
              <span className="anaf-query-pie-chart-legend-label">Equity</span>
            </div>
            <div className="anaf-query-pie-chart-legend-item">
              <span
                className="anaf-query-pie-chart-legend-swatch"
                style={{ backgroundColor: "#E94E3C" }}
              />
              <span className="anaf-query-pie-chart-legend-label">
                Total Liabilities
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClientPortfolioAnafQuery;